import React, { useEffect, useState } from 'react';
import { Form, Input, Button, Typography, message } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store';
import { setAuth } from '../../store/slices/authSlice';
import { signIn } from '../../services/authService';
import AuthPageLayout from '../../components/auth/AuthPageLayout';
import AuthFormContainer, {
  Header,
  FormActions,
  SecurityNote
} from '../../components/auth/AuthFormContainer';
import CustomCheckbox from '../../components/common/CustomCheckbox';
import ButtonGroup from '../../components/common/ButtonGroup';
import {
  saveRememberMe,
  loadRememberMe,
  clearRememberMe,
  isLocalStorageAvailable,
  REMEMBER_ME_STORAGE_KEY
} from '../../utils/storageUtils';

const { Title, Text } = Typography;

interface SignInFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

const SignInPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [isFormValid, setIsFormValid] = useState(false);
  const [loading, setLoading] = useState(false);

  // Load saved email and remember me state on component mount
  useEffect(() => {
    if (!isLocalStorageAvailable()) {
      console.warn('localStorage is not available');
      return;
    }

    const savedData = loadRememberMe();
    if (savedData && savedData.rememberMe) {
      form.setFieldsValue({
        email: savedData.email,
        rememberMe: savedData.rememberMe,
      });
    }
  }, [form]);

  // Handle localStorage changes from other tabs/windows or when cleared
  useEffect(() => {
    if (!isLocalStorageAvailable()) {
      return;
    }

    const handleStorageChange = (e: StorageEvent) => {
      // If our remember me key was removed or changed
      if (e.key === REMEMBER_ME_STORAGE_KEY && !e.newValue) {
        // localStorage was cleared or our key was removed
        form.setFieldsValue({
          email: '',
          rememberMe: false,
        });
        setIsFormValid(false);
      }
    };

    // Listen for storage events (fired when localStorage is modified in another tab/window)
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [form]);

  const handleFormChange = (changedValues: Partial<SignInFormData>) => {
    // If remember me is explicitly unchecked, clear saved data immediately
    if (changedValues.hasOwnProperty('rememberMe') && !changedValues.rememberMe) {
      if (isLocalStorageAvailable()) {
        clearRememberMe();
      }
    }

    const values = form.getFieldsValue(['email', 'password']);
    const email = values.email?.trim() || '';
    const password = values.password?.trim() || '';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isEmailValid = emailRegex.test(email);
    const isPasswordValid = password.length > 0;

    setIsFormValid(isEmailValid && isPasswordValid);
  };

  const handleSubmit = async (values: SignInFormData) => {
    try {
      setLoading(true);
      
      // Sign in (auth info is saved automatically in authService)
      const response = await signIn(
        {
          email: values.email,
          password: values.password,
        },
        values.rememberMe ?? false
      );
      
      // Save remember me preference
      if (isLocalStorageAvailable()) {
        saveRememberMe(values.email, values.rememberMe ?? false);
      }
      
      // Update Redux state
      dispatch(setAuth({ user: response.data.user }));
    } catch (error: any) {
      // Case 1: Password reset required - redirect to change temporary password
      if (error?.code === 'PASSWORD_RESET_REQUIRED') {
        navigate('/auth/change-temporary-password', {
          state: {
            email: values.email,
            currentPassword: values.password,
            rememberMe: values.rememberMe ?? false,
          }
        });
        return;
      }

      // Case 2: Validation error - show inline error message
      if (error?.code === 'VALIDATION_ERROR' && error?.data) {
        const fieldErrors: any[] = [];

        if (error.data.email && Array.isArray(error.data.email)) {
          fieldErrors.push({
            name: 'email',
            errors: error.data.email,
          });
        }

        if (error.data.password && Array.isArray(error.data.password)) {
          fieldErrors.push({
            name: 'password',
            errors: error.data.password,
          });
        }

        if (fieldErrors.length > 0) {
          form.setFields(fieldErrors);
          return;
        }
      }

      // Case 3: Other errors (NOT_AUTHORIZED, etc.) - show toast directly
      message.error(error?.message || 'Sign in failed. Please try again.');
      console.error('Sign in failed:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPageLayout>
      <AuthFormContainer>
        <Header>
          <Title level={2}>Sign in</Title>
          <Text className="secondaryText">
            Sign in to manage, certify, and monitor your organization's internal controls with confidence.
          </Text>
        </Header>

        <Form
          form={form}
          name='signin'
          onFinish={handleSubmit}
          onValuesChange={handleFormChange}
          layout='vertical'
          autoComplete='off'
          requiredMark={false}
        >
          <Form.Item
            name='email'
            label='Email address'
            rules={[
              {
                required: true,
                message: 'Please input your email!',
              },
              {
                type: 'email',
                message: 'Please enter a valid email!',
              },
            ]}
          >
            <Input
              placeholder='name@company.com'
              autoComplete='email'
            />
          </Form.Item>

          <Form.Item
            name='password'
            label='Password'
            rules={[
              {
                required: true,
                message: 'Please input your password!',
              },
            ]}
          >
            <Input.Password
              placeholder='*************'
              autoComplete='current-password'
            />
          </Form.Item>

          <FormActions>
            <Form.Item 
              name='rememberMe' 
              valuePropName='checked'
              style={{ margin: 0 }}
            >
              <CustomCheckbox>Remember me</CustomCheckbox>
            </Form.Item>

            <Link to='/auth/forgot-password' className='forgot-password'>
              Forgot password
            </Link>
          </FormActions>

          <ButtonGroup>
            {/* <Button
              type='default'
              size='large'
            >
              Sign up
            </Button> */}
            <Button
              type='primary'
              htmlType='submit'
              loading={loading}
              disabled={!isFormValid || loading}
              size='large'
            >
              {loading ? 'Signing In...' : 'Log in'}
            </Button>
          </ButtonGroup>
        </Form>

        <SecurityNote>
          <Text className="secondaryText">
            Your information is securely encrypted.
          </Text>
        </SecurityNote>
      </AuthFormContainer>
    </AuthPageLayout>
  );
};

export default SignInPage; 